// WHERE A RECORD LIVES, and how an EPHEMERAL quick view is carried.
//
// A record has one durable address: `/people/{id}`, `/expense/claims/{id}`, and so on.
// That is what a bookmark, copied link, new tab and analytics event name. A quick view is
// different: it is temporary interface state over the page somebody is working on. Its
// stack therefore travels in React Router's `location.state`, which is backed by the
// browser's session-history state and never appears in the address bar or in a cookie.
//
// This keeps the useful browser behaviour without pretending the pane is a second page:
// opening one PUSHES history, Back closes it, and stacked panes unwind one at a time. A
// reload in the same history entry can restore it; copying `/leave` does not copy an open
// colleague pane. Anyone who wants to share the colleague uses the durable `/people/{id}`
// link, which remains the link's real `href` and is what modified-click/new-tab receives.
//
// This file holds no component so address declarations can be used by both the surfaces
// that point at a record and the shell that draws a quick view, without a render cycle.

import type { MessageKey } from "../shell/i18n";

/** What kinds of thing riksu will open a record for. */
export type RecordKind =
  | "person"
  | "colleague"
  | "unit"
  | "job"
  | "position"
  | "workplace"
  | "asset"
  | "advance"
  | "payrollSource"
  | "payrollAnalysis"
  | "period"
  | "posting"
  | "requisition"
  | "application"
  | "claim"
  | "compensation"
  | "compensationStatement"
  | "leaving"
  | "document"
  | "process"
  | "mobilityOpportunity"
  | "companyPolicy"
  | "learningAssignment";

export interface RecordAddress {
  readonly kind: RecordKind;
  /**
   * The route pattern the router serves — `/people/:person_id`.
   *
   * `application-routes.tsx` builds a `<Route>` from it, so `ROUTE_PATHS` carries it and the trail gate
   * walks it like any other address. It resolves to its module by prefix in
   * `destinations.ts`, which is why no destination has to be declared for it: a record is
   * not a rail item, it is a place inside one.
   */
  readonly at: string;
  /** The parameter carrying the identifier, and the segment `at` ends with. */
  readonly by: string;
  /** Static sibling addresses that do not name a record, even when URL-encoded. */
  readonly reservedSegments?: readonly string[];
  /** What the pane is called before its subject has a name, and the screen's own lede. */
  readonly heading: MessageKey;
  /**
   * THIS RECORD'S BODY RENDERS A TABLE, so its pane opens at 2x.
   *
   * Arda's rule and his words: "tablo olanlarda 2x, normallerde 1x". What is declared is
   * the TABLE and never the size — `--frame-pane` is 1x and `--frame-pane-wide` is 2x,
   * `ui/pane.tsx` maps one to the other, and no record gets to have an opinion about
   * pixels. `--frame-pane`'s own comment says why 480 is not enough here: it is the width
   * of the widest FORM riksu has, and a five-column table inside it, less 48px of inset,
   * has 432px to lay out in.
   *
   * It is declared HERE rather than in the body that draws the table because
   * `record-pane.tsx` picks the body from the KIND, so the address declaration is
   * the one place that knows what a reader is about to get. Adding a table-carrying
   * record is this one line and no component.
   *
   * `true` rather than `boolean`, so `carriesATable: false` cannot be written: absent is
   * the answer for a record that does not, and two spellings for one state is the thing a
   * reviewer then has to check for.
   *
   * IT IS CHECKED RATHER THAN TRUSTED. `every_record_that_holds_a_table_says_so` opens
   * each record in `RECORDS` and compares this line against whether a `<table>` actually
   * rendered, in both directions — so a record that grows a table and forgets the line
   * goes red, and so does a line left behind by a body that lost its table.
   */
  readonly carriesATable?: true;
  /** A document is reviewed alongside the record's decisions, using the existing wider pane. */
  readonly comparesDocument?: true;
}

export const RECORDS: readonly RecordAddress[] = [
  {
    kind: "learningAssignment",
    at: "/trainings/applications/:assignment_id",
    by: "assignment_id",
    heading: "learningAssign.detail",
    carriesATable: true,
  },
  {
    kind: "payrollAnalysis",
    at: "/payroll/result-analyses/:analysis_id",
    by: "analysis_id",
    heading: "payrollAnalysis.detail",
    carriesATable: true,
  },
  {
    kind: "payrollSource",
    at: "/payroll/result-imports/:import_id",
    by: "import_id",
    heading: "payrollSource.heading",
    carriesATable: true,
  },
  {
    kind: "companyPolicy",
    at: "/company-policies/:publication_id",
    by: "publication_id",
    heading: "companyPolicies.detailHeading",
    comparesDocument: true,
  },
  {
    kind: "mobilityOpportunity",
    at: "/mobility/opportunities/:opportunity_id",
    by: "opportunity_id",
    heading: "mobility.opportunity.detailHeading",
  },
  {
    kind: "application",
    at: "/recruiting/applications/:application_id",
    by: "application_id",
    heading: "recruiting.detail.heading",
    comparesDocument: true,
  },
  {
    kind: "requisition",
    at: "/recruiting/requisitions/:requisition_id",
    reservedSegments: ["new"],
    by: "requisition_id",
    heading: "record.requisition",
    // The applications tab uses the shared comparison table.
    carriesATable: true,
  },
  {
    kind: "colleague",
    at: "/people/directory/:person_id",
    by: "person_id",
    heading: "record.colleague",
  },
  {
    kind: "person",
    at: "/people/:person_id",
    reservedSegments: ["directory"],
    by: "person_id",
    heading: "record.person",
    // THE ÖZLÜK DOSYASI, and this line is easy to think is wrong. It is not: the person's
    // pane ALREADY draws a table today, before anybody adds an employment history to it —
    // `profile.tsx` renders `<PersonnelFile>`, which is a `DataTable` captioned "Personnel
    // file (özlük dosyası)".
    //
    // DECLARED ON WHAT THE SURFACE CAN DRAW, never on what one reader turned out to get,
    // and the person's record is the case that forces the distinction. Three answers are
    // possible from the same component: `PersonnelFile` returns `null` on a REFUSED
    // document read (a colleague outside every relationship), `DataTable` renders a
    // SENTENCE and no `<table>` once the read settles with no rows, and it renders the
    // table when there are documents. Measured on the dev stack, where the seeded person
    // has none: `GET /v1/documents` answers `200 []` and the pane draws NO table.
    //
    // The width still has to be 2x. It is chosen before any read answers — a width
    // recomputed when a request lands is a pane that jumps sideways under somebody's hands
    // a second after it opened — so the only question it can ask is whether this record
    // draws a table AT ALL, and for a person's file the answer is yes for every reader
    // entitled to it and for every tenant past their first week.
    //
    // It is also the record where 1x was already MEASURED as too narrow, before this rule
    // existed: `personnel-file.tsx` records dropping a THIRD COLUMN because three of them
    // came to 518px inside a 397px pane, so a reader could see a document's kind or the
    // signature it needed and never both. That column is now affordable and putting it
    // back is a separate change.
    carriesATable: true,
  },
  // THE THREE IMPERSONAL ONES sit under `/organisation` and not under `/setup`. See the
  // note at the top of this file: `/setup` is where these are created and takes a role,
  // and the reader pointing at a department name on the roster holds none.
  //
  // All three are `Subject::Impersonal` on the server, because a seat exists whether or
  // not anybody is in it — so listing them discloses the shape of the company and nothing
  // about who works there. Occupancy is the one join that would name somebody and is
  // deliberately absent from all three.
  {
    kind: "unit",
    at: "/organisation/units/:unit_id",
    by: "unit_id",
    heading: "record.unit",
    // `Seats` — the seats in this department, by job, with riksu's three figures.
    carriesATable: true,
  },
  {
    kind: "job",
    at: "/organisation/jobs/:job_id",
    by: "job_id",
    heading: "record.job",
    // `Seats` again, the other way round: the departments this classification is held in.
    carriesATable: true,
  },
  {
    // NO TABLE, and it is the honest answer rather than an oversight. A seat's record is
    // seven facts and a sentence — riksu serves no read for who is IN it, which the pane
    // says in words — so there is nothing here to lay out across.
    kind: "position",
    at: "/organisation/positions/:position_id",
    by: "position_id",
    heading: "record.position",
  },
  // A REGISTERED WORKPLACE, and it is the reference that was named twice and openable
  // neither time. `/setup/positions` prints the workplace a seat is declared under and
  // `/setup/workplaces` is the register — the fourth organisational object, with three of
  // its siblings addressable and this one not.
  //
  // THE FIRST VERSION OF THIS COMMENT NAMED `/organisation/workplaces` AS THE REGISTER, and
  // that address does not exist: the twin was deliberately removed on 2026-08-07 and
  // `destinations.ts:284` serves `/setup/workplaces` alone. The old fallback-page declaration
  // was wrong with it and threw at import, which took nine test files down at collection. The
  // rule that would have prevented it: `records.ts` cannot tell you what `destinations.ts`
  // serves, so an address asserted here is checked against the file that owns it BEFORE it
  // is written, not after a guard catches it.
  //
  // IT SITS UNDER `/setup`, WHICH IS THE OPPOSITE OF WHAT THE UNIT, JOB AND SEAT DO — and
  // the difference is the rule at the top of this file working rather than bending.
  //
  // Those three are filed under `/organisation` because their register is a `/setup` step
  // gated to `organization.admin`, and they are pointed at from `/people`: a record filed
  // behind a gate the reader does not hold is unopenable by exactly the person who pressed
  // the cell. **A workplace is pointed at from nowhere else.** Its two call sites are
  // `/setup/positions` and `/setup/workplaces`, both inside that same gate — so anybody who
  // can reach a cell naming a workplace can already reach `/setup`, and there is no reader
  // to strand. The gate that argued the other three OUT of `/setup` does not bite here.
  //
  // And the register really is a `/setup` step: it had a twin at `/organisation/workplaces`
  // for part of 2026-08-07, the product owner found it himself and settled it — *"olması
  // gereken yerde bir tane olsun yeter"* — so inventing a second address here to hang a
  // record off would be re-opening a question he closed. The canonical record route extends
  // the register's existing path.
  //
  // NO NEW ROUTE AND NO NEW DECLARATION. `GET /v1/organization/workplaces` is already
  // `Subject::Impersonal` and its stated reason holds unchanged for this record: a
  // workplace is an SGK registration, a name, a hazard class and a legal entity, and
  // occupancy is the join that would name somebody and is not there. Like the unit, job
  // and seat, the record is FOUND IN A LIST — riksu serves no read for one workplace.
  {
    kind: "workplace",
    at: "/setup/workplaces/:workplace_id",
    by: "workplace_id",
    heading: "record.workplace",
    // NO TABLE. Seven facts and a sentence: the registration, the hazard class, the
    // entity, and riksu's four md. 30 figures. The quota is a FIGURE rather than a list of
    // who fills it — riksu answers no such list — so there is nothing to lay out across.
  },
  // AN ASSET SITS UNDER ITS OWN CATALOGUE, which is the one case where the module and the
  // list agree at a level below the module: `/assets` is four screens and the catalogue is
  // the one that holds the item. Its canonical record path therefore extends the catalogue
  // route rather than the module root.
  {
    kind: "asset",
    at: "/assets/catalogue/:asset_id",
    by: "asset_id",
    heading: "record.asset",
  },
  // The original advance address now carries chronological financial children.
  // Its admitted detail reuses the original balance projection and owns one table.
  {
    kind: "advance",
    at: "/advance/balances/:cash_advance_id",
    by: "cash_advance_id",
    heading: "record.advance",
    carriesATable: true,
  },
  // A PAYROLL MONTH, and the record is the only place the drift report is reachable from.
  //
  // `GET /v1/payroll/periods/{period_id}/drift` is the operation this module exists for
  // and it names a `period_id` in its path, so it was addressable only by an identifier
  // nothing in the product could produce. Making the month a RECORD is what gives that
  // read an address: the reader presses the month on the list and the pane holds what has
  // moved inside it.
  //
  // `/payroll/periods/{id}` rather than `/payroll/{id}`, mirroring the API. The bare form
  // would work today and would read the first section anybody ever adds under `/payroll`
  // as a period identifier — `recordOpenAt` matches a prefix and one segment, and it
  // cannot tell a word from a UUID.
  {
    kind: "period",
    at: "/payroll/periods/:period_id",
    by: "period_id",
    heading: "record.period",
    // THE RECORD THE SECOND WIDTH EXISTS FOR. The drift report is five columns and not
    // one of them is optional to the reader: WHO was paid wrongly, WHICH DAY moved, which
    // table it moved in, whether it was recorded or withdrawn, and who recorded it. At 1x
    // the source table wraps to three lines and the person's name to two, so a row —
    // which is one worker's pay being wrong — stops reading as a row.
    carriesATable: true,
  },
  // ONE JOB POSTING. The register carries the comparison fields; the single read adds the
  // advertisement exactly as written. That paragraph is the reason this is a record rather
  // than a larger hover card, and pressing the row is its ordinary entrance.
  {
    kind: "posting",
    at: "/recruiting/postings/:job_posting_id",
    by: "job_posting_id",
    heading: "record.posting",
  },
  // ONE EXPENSE CLAIM, and the record that closed the LAST second detail surface in the
  // product.
  //
  // `/expense` drew its own pane region — a local `Opened` component driven by a local
  // `useState` and an `onOpen` row action — BELOW the claims table. The product owner
  // named both halves of what was wrong with it: *"open'a basıyoruz ya mesela quick
  // view'de sidepane'de açılmıyor aşağı gidiyor mesela sağdan çıkmalı"* (it goes DOWN
  // where it should come from the right), and *"daha geniş olmalı buradaki çünkü çok daha
  // detaylı olabilir bazı şeyler, tablo falan olduğu için"* (it must be wider, because it
  // carries a table). Both are answered by being a record: the shell's one pane draws it,
  // and the line below decides the width.
  //
  // `/expense/claims/{id}` MIRRORS THE API and is the module that OWNS the object, which
  // is the rule at the top of this file rather than a coincidence here. The claims table
  // is the expense module's screen, so "the list it was opened from" would give the same
  // answer today — and it would stop being the same answer the moment a claim is pointed
  // at from the approver's inbox, which is exactly where `reads/expense.ts` already reads
  // one. The bare `/expense/{id}` would also work today and would read the first section
  // anybody ever adds under `/expense` as a claim identifier: `recordOpenAt` matches a
  // prefix and one segment and cannot tell a word from a UUID, and `/expense/requests`
  // is that word already. (`/expense/advances` was the word this named until the avans
  // became its own module at `/advance`; the argument is unchanged and its example moved.)
  {
    kind: "claim",
    at: "/expense/claims/:claim_id",
    by: "claim_id",
    heading: "record.claim",
    // THE ITEMS ON THE CLAIM: date, category, description, receipt reference and the two
    // money columns. Six, of which the description absorbs the slack and the receipt
    // reference is the value somebody reads off a piece of paper — the exact shape the
    // product owner asked for the second width for.
    //
    // DECLARED ON WHAT THE SURFACE CAN DRAW, never on what one reader turned out to get,
    // which is the rule the person's record above states at length. A claim with no lines
    // yet renders a sentence and no `<table>` — and it is a claim somebody is about to put
    // an item on, so the width is chosen for the claim it becomes rather than for the
    // first frame of it. A width recomputed when a read lands is a pane that jumps
    // sideways under somebody's hands.
    carriesATable: true,
  },
  {
    kind: "compensation",
    at: "/compensation/employments/:employment_id",
    by: "employment_id",
    heading: "record.compensation",
    // Recurring components and issued change statements are both registers on this record.
    carriesATable: true,
  },
  {
    kind: "compensationStatement",
    at: "/compensation/statements/:statement_id",
    by: "statement_id",
    heading: "record.compensationStatement",
    // The evidence timeline is a real table, even when one new statement has one event.
    carriesATable: true,
  },
  // ONE LEAVING, and the second screen-owned detail surface to become a place.
  //
  // `/offboarding` drew its own `<Pane open={selected !== null}>` holding a `Leaving` —
  // right about the SHAPE (its comment argues a pane over an expanding row, and it is
  // correct) and wrong about the mechanism: it was opened by state rather than by an
  // address, so a clearance nobody could bookmark, paste into a message or close with
  // Back. It also carried the defect the product owner named — *"bunda Close zaten sağ
  // üstte var kankam X olarak, tekrar close butonuna bence gerek yok"* — because a pane
  // with `onSubmit` closing it draws a Close beside the corner X. `reading` is what
  // removes both, and a record gets it by construction.
  //
  // `/offboarding/{id}` mirrors `GET /v1/offboarding/{offboarding_id}` and is the module
  // that owns the object; `/offboarding` serves the list, so the prefix-and-one-segment
  // match `recordOpenAt` performs cannot collide with anything the router serves.
  //
  // NO `carriesATable`, and it is MEASURED rather than assumed by analogy with the claim.
  // The body draws two `<ul>`s — the blockers and the tasks — and no `DataTable` anywhere,
  // so it opens at 1x. `every_record_that_holds_a_table_says_so` is what holds it to that
  // in both directions: the day somebody makes the task checklist a real table, the walk
  // goes red and asks for this line.
  {
    kind: "leaving",
    at: "/offboarding/:offboarding_id",
    by: "offboarding_id",
    heading: "record.leaving",
  },
  // ONE DOCUMENT, and the record that closed the row that opened nothing.
  //
  // The özlük dosyası has listed a person's documents since md. 75 got a surface, and
  // pressing a row did nothing at all — not a dead link, no affordance whatever, because
  // `PersonnelFile`'s columns declared no reference. So a file an inspector reads along was
  // a list of titles with no way to ask what any of them WAS: which signature its class
  // needs under 5070 md. 5, whether anything meets that, and whether riksu has looked at
  // the bytes the signature would be about.
  //
  // THE ORDER THIS ARRIVED IN IS FORCED AND IS WORTH RECORDING, because the one-line fix
  // looks available from the start and is not. Its canonical path needs a module the router
  // serves, so this entry could not exist before `/documents` did; and a record resolves to
  // its module BY PREFIX, so `/documents/{id}` had an empty trail until `/documents` was in
  // `destinations.ts`. The row that opens nothing is the LAST step of three, not the first.
  //
  // `/documents/{id}` MIRRORS THE API and is the module that owns the object, which is the
  // rule at the top of this file rather than a coincidence here — and this is the case that
  // shows why the rule is not "the list it came from". A document is pointed at from THREE
  // lists already: the özlük dosyası on a person's record, and both chase lists on
  // `/documents`. "The list it was opened from" would give one object three addresses, which
  // is the department's argument arriving for a second object.
  //
  // NO SECTION SEGMENT, unlike `/expense/claims/{id}` and `/payroll/periods/{id}`. Those two
  // carry one because their module has sections a bare identifier could collide with —
  // `recordOpenAt` matches a prefix and one segment and cannot tell a word from a UUID, and
  // `/expense/requests` is that word already. `/documents` is ONE screen with no sections
  // and none proposed: the two chase lists are two tables on it rather than two addresses,
  // because they are one question asked twice and a reader wants both in view. The day a
  // section is added under `/documents`, this address has to grow a segment with it.
  {
    kind: "document",
    at: "/documents/:document_id",
    by: "document_id",
    heading: "record.document",
    // THE SIGNATURES ON IT. Four columns and two of them are long: who signed, with what
    // strength, when, and the `evidence_reference` — the certificate serial for a qualified
    // signature, where the scanned original is for wet ink — which is the value somebody
    // reads back off a piece of paper and the exact shape the second width exists for.
    //
    // DECLARED ON WHAT THE SURFACE CAN DRAW, never on what one reader turned out to get,
    // which is the rule the person's record states at length and which bites hardest here:
    // most documents reachable from `/documents` are UNSIGNED, because being unsigned is
    // what puts them on that screen, and an unsigned one renders a sentence and no `<table>`.
    // The width is still chosen before any read answers — a width recomputed when a request
    // lands is a pane that jumps sideways under somebody's hands — so the only question it
    // can ask is whether this record draws a table AT ALL, and a document that carries a
    // signature is exactly the one somebody opened this to check.
    carriesATable: true,
  },
  // ONE PENDING REQUEST, and the record that answers the question the product owner asked
  // in as many words: *who is going to approve this*.
  //
  // IT SITS UNDER `/inbox/tasks`, which is the module section that owns the object rather than the list it
  // is opened from — the rule at the top of this file, and the case that shows why it is not
  // "the list it came from": a process is pointed at from the inbox, from the submissions
  // list, and from every module screen that grows a *what I sent* view. Three lists, and one
  // address. `/inbox/tasks` is in `ALONGSIDE` rather than `DESTINATIONS`, which `moduleFor`
  // searches too, so the trail resolves like any other module's.
  //
  // NOT ROLE-GATED, matching `/inbox/tasks` itself: the record narrows on the SERVER to whoever is
  // party to the process — the assignee or the initiator — so a reader holding nothing sees
  // their own requests and a 404 for anybody else's. A role gate here would hide the record
  // from exactly the person it was built for, who is somebody with no roles asking where
  // their leave went.
  //
  // `/inbox/tasks/processes/{id}` rather than `/inbox/tasks/{id}`, for the reason `/expense/claims/{id}`
  // and `/payroll/periods/{id}` both carry a segment: `recordOpenAt` matches a prefix and one
  // segment and cannot tell a word from a UUID, so the bare form would read the first section
  // anybody ever adds under `/inbox/tasks` as a process identifier.
  //
  // NO `carriesATable`, and it is measured rather than assumed by analogy. The body draws
  // `Facts` and an `<ol>` of decided steps — the history is a short list of sentences with a
  // note under each, not a grid — so it opens at 1x, and
  // `every_record_that_holds_a_table_says_so` holds it to that in both directions.
  {
    kind: "process",
    at: "/inbox/tasks/processes/:process_id",
    by: "process_id",
    heading: "record.process",
  },
];

/**
 * THE ADDRESS OF ONE RECORD, which is what a column's `detail` answers with.
 *
 * A function rather than a template at each call site, so the pattern and the address are
 * derived from ONE declaration: a route the router serves and a link nothing serves is
 * exactly the pair that cannot be told apart by reading either half.
 *
 * The identifier is encoded. Every one riksu issues is a UUID and none of them needs it —
 * which is why it is done here, where the one that does not fit that assumption arrives
 * safely, rather than at forty call sites where it would not.
 */
export function recordOf(kind: RecordKind): RecordAddress {
  const spec = RECORDS.find((candidate) => candidate.kind === kind);
  if (!spec) {
    throw new Error(`no record kind named ${kind}`);
  }
  return spec;
}

export function recordAt(kind: RecordKind, id: string): string {
  const spec = recordOf(kind);
  return spec.at.replace(`:${spec.by}`, encodeURIComponent(id));
}

/** Our namespaced member inside React Router's `location.state`. */
const RECORD_STATE = "riksu.quickViews";
const REVIEW_STATE = "riksu.reviewSequences";

type HistoryState = Readonly<Record<string, unknown>>;

function historyState(value: unknown): HistoryState {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as HistoryState)
    : {};
}

/** The durable record addresses carried by an invisible quick-view state. */
export function recordAddressesIn(state: unknown): readonly string[] {
  const value = historyState(state)[RECORD_STATE];
  return Array.isArray(value) ? value.filter((one): one is string => typeof one === "string") : [];
}

/**
 * WHICH RECORDS HISTORY STATE OPENS, bottom of the stack first, and `[]` for a plain page.
 *
 * Read through `recordOpenAt`, so there is one parser for what a record address is. A value
 * that names no record is dropped rather than trusted. History state is written by this
 * client, but it can still be stale after a deploy or malformed by an extension; neither
 * case gets an empty pane.
 */
export function recordsOpenIn(state: unknown): OpenRecord[] {
  return recordAddressesIn(state)
    .map((address) => recordOpenAt(address))
    .filter((found): found is OpenRecord => found !== null);
}

/** A captured loaded window is a navigation hint; each destination still reads with caller authority. */
export function reviewSequenceIn(state: unknown, address: string): readonly string[] {
  const record = recordOpenAt(address);
  const value = historyState(historyState(state)[REVIEW_STATE])[address];
  if (!record || !Array.isArray(value) || !value.includes(address)) return [];
  if (
    !value.every(
      (one) => typeof one === "string" && recordOpenAt(one)?.spec.kind === record.spec.kind,
    )
  )
    return [];
  return [...new Set(value as string[])];
}

/**
 * ONE NAVIGATION THAT OPENS RECORDS WITHOUT PUTTING THEM IN THE VISIBLE URL.
 *
 * `to` keeps the page, including its own query. `state` preserves every unrelated object
 * member already carried by the history entry and replaces only this feature's namespaced
 * stack. A cookie is deliberately not involved: this state is local to one tab and one
 * history entry and has no work for the server to do.
 *
 * Returning the pair keeps `Link` and programmatic `navigate` on the same mechanism.
 */
export function openingOver(
  page: string,
  records: readonly string[],
  currentState: unknown = null,
  reviewSequence?: readonly string[] | null,
): { readonly to: string; readonly state: HistoryState | null } {
  const next = { ...historyState(currentState) };
  const reviews = { ...historyState(next[REVIEW_STATE]) };
  const top = records.at(-1);
  if (top && reviewSequence) reviews[top] = [...reviewSequence];
  // Closing/clamping a level also releases its captured window; unrelated history survives.
  for (const address of Object.keys(reviews)) {
    if (!records.includes(address)) delete reviews[address];
  }
  if (Object.keys(reviews).length) next[REVIEW_STATE] = reviews;
  else delete next[REVIEW_STATE];
  if (records.length === 0) {
    delete next[RECORD_STATE];
  } else {
    next[RECORD_STATE] = [...records];
  }
  return {
    to: page,
    state: Object.keys(next).length === 0 ? null : next,
  };
}

/** One record, open at an address. */
export interface OpenRecord {
  readonly spec: RecordAddress;
  readonly id: string;
}

/**
 * WHICH RECORD AN ADDRESS NAMES, or `null` for an address that names none.
 *
 * Matched by hand rather than through `useParams`: quick-view state carries durable record
 * addresses, but there is no router matching values inside history state. A record's pattern
 * is a literal prefix and one segment, so the match is a prefix test and a non-empty tail;
 * anything deeper is a different address and not a record with a suffix.
 *
 * It reads the same PATHNAME that the durable route serves, so quick views and full screens
 * cannot develop two spellings for one record.
 */
export function recordOpenAt(pathname: string): OpenRecord | null {
  for (const spec of RECORDS) {
    const prefix = spec.at.slice(0, spec.at.lastIndexOf("/") + 1);
    if (!pathname.startsWith(prefix)) {
      continue;
    }
    const tail = pathname.slice(prefix.length);
    if (tail !== "" && !tail.includes("/")) {
      const id = decodeURIComponent(tail);
      return spec.reservedSegments?.includes(id) ? null : { spec, id };
    }
  }
  return null;
}
